import {
  Component,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  TemplateRef,
} from "@angular/core";
import {
  MatRowDef,
  MatTableDataSource,
  MatTableModule,
} from "@angular/material/table";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { MatMenuModule } from "@angular/material/menu";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { CompanyAttributes, DocumentDetailClassAttributes, DocumentDetailTypeAttributes } from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { Subscription } from "rxjs";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { ColDef, GridConfig, GridReadyEvent, SmartGridComponent, SortChangedEvent } from "app/shared/components/smart-grid";
import { IPaginationResult, IRequestResult } from "app/common/interfaces";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DocumentLineClassService } from "./document-class-line.service";
import { TypeLineCellRendererComponent } from "./cell-renderers/type-cell-renderer.component";
import { StatusCellRendererComponent } from "./cell-renderers/status-cell-renderer.component";
import { ActionsCellRendererComponent } from "./cell-renderers/action-cell-renderer.component";
import { CustomDialogComponent } from "app/shared/components/custom-dialog/custom-dialog.component";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { FormSectionComponent } from "app/shared/components/form-ui/form-section/form-section.component";
import { ToggleOptionComponent } from "app/shared/components/form-ui/toggle-option/toggle-option.component";
import { ConfigurationFiltersComponent } from "../../filters/configuration-filters/configuration-filters.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { DocumentsFiltersComponent } from "../../filters/documents-filters/documents-filters.component";
import { FormDocumentClassLineComponent } from "./form-document-class-line/form-document-class-line.component";

@Component({
  selector: "app-document-class-line",
  templateUrl: "./document-class-line.component.html",
  styleUrls: ["./document-class-line.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    MatRowDef,
    MatSidenav,
    MatSidenavContainer,
    StatusFiltersComponent,
    HoverMenuDirective,
    SmartGridComponent,
    FormSectionComponent,
    ReactiveFormsModule,
    MatSelectModule,
    MatInputModule,
    MatDialogModule,
    ToggleOptionComponent,
    FilterComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
})
export class DocumentClassLineComponent implements AfterViewInit, OnDestroy {
  StatusEnum = StatusEnum

  dataSource = new MatTableDataSource<DocumentDetailClassAttributes>([]);

  searchQuery: string = "";
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof DocumentDetailClassAttributes = "name";
  sortDirection: "ASC" | "DESC" = "DESC";

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;

  //#region Smart Grid
  // Using `any` because doesn't support DocumentDetailClassAttributes.
  columnDefs: ColDef<any>[] = [ //DocumentDetailClassAttributes
    {
      field: 'code',
      headerName: 'Código',
      autoSize: true,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Nombre',
      autoSize: true,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'documentDetailType',
      headerName: 'Tipo',
      cellRenderer: TypeLineCellRendererComponent,
      autoSize: true,
      minWidth: 200,
      sortable: true,
    },
    {
      field: 'statusId',
      headerName: 'Estado',
      cellRenderer: StatusCellRendererComponent,
      autoSize: true,
      minWidth: 220,
      sortable: true,
      cellRendererParams: {
        onStatusChange: (checked: boolean, data: DocumentDetailClassAttributes) => {
          this.onStatusChange(checked, data);
        }
      },
    },
    {
      headerName: 'Acciones',
      cellRenderer: ActionsCellRendererComponent,
      sortable: false,
      autoSize: true,
      minWidth: 120,
      colId: 'actions',
      pinned: 'right',
      cellRendererParams: {
        editMenuItem: (rowData: DocumentDetailClassAttributes) => {
          this.openModal(rowData)
        },

        deleteMenuItem: (rowData: DocumentDetailClassAttributes) => {
          this.openConfirmDialog(rowData)
        }
      }
    },
  ]

  gridConfig: GridConfig<any> = { //DocumentDetailClassAttributes
    emptyState: {
      message: 'No encontramos tus resultados!',
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: 'api',
      multiColumn: false,
      defaultSort: [{ field: this.sortActive, direction: this.sortDirection == 'ASC' ? 'asc' : 'desc' }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: 'local'
    },
    columnVisibility: {
      enabled: false,
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40,
    },
    styling: {
      maxHeight: '85vh',
      density: 'comfortable',
      fontSize: '12px',
      cellPaddingX: '12px',
      cellPaddingY: '12px',
    },
    settingsPanel: {
      enabled: true,
      position: 'right',
      enableColumnVisibility: true,
      enableDensity: false,
      enableExport: true,
      defaultDensity: 'comfortable',
    },
  };
  //#endregion

  //BUSCADOR
  searchSubscription: Subscription;

  // Status Filter Properties
  formFilter: FormGroup;
  loadFiltersData = false;
  private companiesLoaded = false;
  selectedStatuses: number[] = [StatusEnum.ACTIVE, StatusEnum.INACTIVE]; // Default to show both active and inactive

  constructor(
    private readonly documentLineClassService: DocumentLineClassService,
    private readonly searchService: SearchService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private fb: FormBuilder,
  ) { 
    this.formFilter = this.fb.group({
      companyId: null,
      documentTypeLineId: [],
    });
  }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.findAllWithCount();
  }

  findAllWithCount(): void {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.searchQuery,
      sort: this.sortActive,
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      companyId: this.formFilter.get("companyId")?.value,
      documentTypeLineId: this.formFilter.get("documentTypeLineId")?.value,
      status: this.selectedStatuses.length > 0 ? this.selectedStatuses : null,
    };

    this.documentLineClassService
      .findAllWithCount(options, filter)
      .subscribe({
        next: (res: IPaginationResult<DocumentDetailClassAttributes>) => {
          this.dataSource.data = res?.data?.[0]?.data ?? [];
          this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener tipos de línea",
            "Cerrar",
            { duration: 5000 }
          );
        },
      })
  }

  changeSort(event: SortChangedEvent<any>) { //DocumentDetailClassAttributes
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as any;
      this.sortDirection = sortState.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.sortActive = 'name';
      this.sortDirection = 'DESC';
    }
    this.findAllWithCount();
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.findAllWithCount();
  }

  onGridReady(event: GridReadyEvent<DocumentDetailClassAttributes>): void {
    setTimeout(() => {
      event.api.autoSizeAllColumns();
    }, 100);
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.pageIndex = 1;
          this.findAllWithCount();
        }
      });
  }

  // Status Filter Methods
  onFilterPanelOpened(): void {
    this.loadFiltersData = true;
  }

  onClearFilter() {
    this.formFilter.reset({
      companyId: null,
      documentTypeLineId: [],
    });
    this.findAllWithCount();
  }
  
  applyStatusFilter(): void {
    this.pageIndex = 1;
    this.findAllWithCount();
  }

  //#region Action
  openModal(document?: DocumentDetailClassAttributes): void {
    const dialogRef = this.dialog.open(FormDocumentClassLineComponent, {
      width: "70%",
      data: document,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.findAllWithCount()
      }
    });
  }

  onStatusChange(event: boolean, document: DocumentDetailClassAttributes): void {
    const originalStatusId = document.statusId;
    const updatedStatusId = event ? StatusEnum.ACTIVE : StatusEnum.INACTIVE;
    const updatedDocumentClass = {
      ...document,
      statusId: updatedStatusId,
    };

    this.documentLineClassService.update(document._id.toString(), updatedDocumentClass).subscribe({
      next: () => {
        this.findAllWithCount();
      },
      error: () => {
        document.statusId = originalStatusId;
        this.snackBar.open("Error al actualizar el estado.", "Cerrar", {
          duration: 3000,
        });
        this.findAllWithCount();
      },
    });
  }

  openConfirmDialog(rowData: DocumentDetailClassAttributes) {
    const dialogRef = this.dialog.open(CustomDialogComponent, {
      data: {
        title: "Confirmar eliminación",
        message: "¿Está seguro que desea eliminar el registro?",
        cancel: 'Conservar',
        yes: 'Eliminar'
      },
      panelClass: "delete-modal",
      disableClose: true,
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.delete(String(rowData._id));
      }
    });
  }

  delete(id: string): void {
    this.documentLineClassService.remove(id).subscribe({
      next: () => {
        this.snackBar.open('Registro eliminado con éxito', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
      },
      error: (err) => {
        this.snackBar.open(err.error?.message || 'Error al eliminar el registro', 'Cerrar', { duration: 5000 });
      }
    });
  }
  //#endregion

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
